import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';

import LoadingSpinner from '@/components/feedback/LoadingSpinner';
import { pathFor } from '@/data/routes';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '@/i18n';

/**
 * Shared newsletter form body — used by Banner / Inline / Sticky / ExitIntent.
 * Migrated teal → bordeaux (mai 2026).
 */
export default function NewsletterForm({ form, cta, tone = 'light', compact = false }) {
  const { t, i18n } = useTranslation('components');
  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;
  const dark = tone === 'dark';
  // Plusieurs formulaires peuvent coexister sur une page (article : 2 NewsletterInline) :
  // des id fixes dupliqués faisaient pointer le <label> et aria-describedby sur le 1er champ.
  const uid = useId();
  const ids = { email: `${uid}-email`, website: `${uid}-website`, error: `${uid}-error` };

  const inputCls = dark
    ? 'border-cream-50/30 bg-navy-800 text-cream-50 placeholder-cream-50/60 focus:border-cream-50 focus:ring-cream-50/20'
    : 'border-navy-900/15 bg-cream-50 text-navy-900 placeholder-gray-500 focus:border-bordeaux-600 focus:ring-bordeaux-600/30 dark:border-white/15 dark:bg-zinc-900 dark:text-white dark:placeholder-white/40 dark:focus:border-bordeaux-400 dark:focus:ring-bordeaux-400/20';

  if (form.status === 'success') {
    return (
      <div role="status" className="flex items-start gap-2 text-sm">
        <CheckCircle2 size={18} className={dark ? 'mt-0.5 shrink-0 text-success-bg' : 'mt-0.5 shrink-0 text-success dark:text-gold-400'} aria-hidden="true" />
        <div>
          <p className={`font-semibold ${dark ? 'text-cream-50' : 'text-success dark:text-gold-400'}`}>{t('newsletter.successTitle')}</p>
          <p className={dark ? 'text-cream-50/80' : 'text-gray-700 dark:text-white/70'}>{t('newsletter.successBody')}</p>
        </div>
      </div>
    );
  }

  if (form.status === 'already') {
    return (
      <div role="status" className="flex items-start gap-2 text-sm">
        <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-bordeaux-700 dark:text-bordeaux-400" aria-hidden="true" />
        <div>
          <p className={`font-semibold ${dark ? 'text-cream-50' : 'text-navy-900 dark:text-white'}`}>{t('newsletter.alreadyTitle')}</p>
          <a
            href={pathFor('newsletter', lang)}
            className={dark ? 'text-cream-50 underline' : 'text-bordeaux-700 underline hover:text-bordeaux-800 dark:text-bordeaux-400 dark:hover:text-bordeaux-300'}
          >
            {t('newsletter.alreadyBody')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={form.submit} noValidate className={compact ? 'space-y-2' : 'sm:flex sm:gap-2'}>
      <label htmlFor={ids.email} className="sr-only">{t('newsletter.emailLabel')}</label>
      {/* Honeypot : hidden from humans (sr-only + tabIndex -1 + autoComplete off
          + aria-hidden). Bots typically fill any input named `website`. If
          filled, the submit handler fakes success without calling the API. */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor={ids.website}>Website (leave empty)</label>
        <input
          id={ids.website}
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.honeypot ?? ''}
          onChange={(e) => form.setHoneypot?.(e.target.value)}
        />
      </div>
      <input
        id={ids.email}
        type="email"
        required
        autoComplete="email"
        value={form.email}
        onChange={(e) => form.setEmail(e.target.value)}
        placeholder={t('newsletter.emailPlaceholder')}
        aria-invalid={form.status === 'error'}
        aria-describedby={form.errorKey ? ids.error : undefined}
        disabled={form.status === 'loading'}
        className={`min-h-[44px] w-full rounded-btn border px-3 py-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-bordeaux-600 focus:ring-offset-2 disabled:opacity-50 ${inputCls} ${compact ? '' : 'sm:flex-1'}`}
      />
      <button
        type="submit"
        disabled={form.status === 'loading'}
        className={`btn-primary mt-2 w-full sm:mt-0 sm:w-auto ${compact ? '' : 'sm:shrink-0'}`}
      >
        {form.status === 'loading' ? (
          <>
            <LoadingSpinner size={16} color="white" label={t('newsletter.submitting')} />
            {t('newsletter.submitting')}
          </>
        ) : (
          <>
            {cta ?? t('newsletter.cta')}
            <ArrowRight size={14} aria-hidden="true" />
          </>
        )}
      </button>
      {form.status === 'error' && form.errorKey && (
        <p id={ids.error} role="alert" className="mt-2 flex items-start gap-2 text-sm text-danger sm:basis-full">
          <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{t(`newsletter.${form.errorKey}`)}</span>
        </p>
      )}
    </form>
  );
}
